import { useTranslation } from "react-i18next";
import { UploadCloud, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { BRANDING_PLACEHOLDER } from "../video-form-utils";

interface TemplateManagementProps {
  templates: any[];
  watermarkPreview: string | null;
  handleWatermarkUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSelectTemplate: (template: any) => void;
  isSubmitting?: boolean;
  setIsSaveTemplateDialogOpen: (open: boolean) => void;
  watermarkUrl: string | null;
  activeTemplate: any;
  handleUpdateTemplate?: () => void;
  isMobile?: boolean;
}

export const TemplateManagement = ({
  templates,
  watermarkPreview,
  handleWatermarkUpload,
  handleSelectTemplate,
  isSubmitting,
  setIsSaveTemplateDialogOpen,
  watermarkUrl,
  activeTemplate,
  handleUpdateTemplate,
  isMobile = false,
}: TemplateManagementProps) => {
  const { t } = useTranslation();

  const MobileNoteBanner = () => (
    <div className="p-4 mb-6 bg-blue-50 border border-blue-200 rounded-2xl flex gap-3 items-center text-blue-800 text-xs 2k:text-lg 4k:text-2xl font-medium">
      <span className="shrink-0 font-bold bg-blue-100 rounded-full px-2.5 py-0.5">Note</span>
      <p>{t("createVideo.mobileBrandingNote") || "Branding templates can only be added or edited using the desktop version of the app."}</p>
    </div>
  );

  if (!watermarkPreview) {
    return (
      <div className="w-full">
        {isMobile && <MobileNoteBanner />}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {/* Upload Card */}
          {!isMobile && (
            <label
              htmlFor="watermark-upload"
              className="aspect-square flex flex-col items-center justify-center p-6 border-2 border-dashed border-black/10 rounded-3xl cursor-pointer hover:bg-black hover:text-white transition-all group bg-white/50"
            >
              <div className="w-12 h-12 rounded-full bg-black/5 flex items-center justify-center mb-3 group-hover:bg-white/20 transition-all">
                <UploadCloud className="w-6 h-6 text-black group-hover:text-white" />
              </div>
              <p className="text-sm font-bold text-center">
                {t("createVideo.clickToUpload")}
              </p>
              <p className="text-xs opacity-60 mt-1">PNG, JPG</p>
              <Input
                id="watermark-upload"
                type="file"
                className="hidden"
                onChange={handleWatermarkUpload}
                accept="image/png, image/jpeg"
                disabled={isSubmitting}
              />
            </label>
          )}

          {/* Template Cards */}
          {templates.map((template) => (
            <button
              key={template.id}
              type="button"
              onClick={() => handleSelectTemplate(template)}
              className="aspect-square flex flex-col p-4 bg-white border border-black/5 rounded-3xl hover:border-black hover:shadow-xl transition-all group text-left"
            >
              <div className="flex-1 rounded-2xl bg-gray-50 flex items-center justify-center overflow-hidden mb-3 border border-black/5 p-4">
                <img
                  src={template.watermarkUrl}
                  alt={template.name}
                  className="max-w-full max-h-full object-contain group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <p className="font-bold text-sm truncate">
                {template.name}
              </p>
              <p className="text-xs text-muted-foreground tracking-widest opacity-40">
                Template
              </p>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {isMobile && <MobileNoteBanner />}
      <div className="flex flex-col md:flex-row md:items-center justify-between p-6 bg-white border border-black/5 rounded-3xl shadow-sm">
        <div className="flex items-center gap-6">
          <div className="w-16 h-16 rounded-2xl bg-gray-50 border border-black/5 flex items-center justify-center overflow-hidden p-2">
            <img
              src={watermarkPreview}
              className="max-w-full max-h-full object-contain"
            />
          </div>
          <div className="flex-1">
            <Select
              value={activeTemplate?.id || "custom"}
              onValueChange={(val) => {
                if (val === "custom") {
                  handleSelectTemplate(null);
                  return;
                }
                const template = templates.find((t) => t.id === val);
                if (template) handleSelectTemplate(template);
              }}
            >
              <SelectTrigger className="w-full border-none bg-transparent p-0 h-auto focus:ring-0 shadow-none hover:opacity-70 transition-opacity">
                <div className="text-left">
                  <p className="font-bold text-lg leading-tight">
                    {activeTemplate?.name || "Default"}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t("createVideo.activeWatermark") || "Active Logo"}
                  </p>
                </div>
              </SelectTrigger>
              <SelectContent className="bg-white/95 backdrop-blur-md border-black/10 rounded-2xl shadow-2xl">
                {!isMobile && (
                  <SelectItem 
                    value="custom" 
                    data-hide-indicator="true"
                    className="py-3 px-4 rounded-xl focus:bg-black/5 data-[state=checked]:bg-black data-[state=checked]:text-white pr-4 transition-colors"
                  >
                    <div className="flex items-center gap-3 text-left">
                      <div className="size-8 rounded-lg bg-black/5 flex items-center justify-center overflow-hidden p-1 border border-black/5 group-data-[state=checked]:bg-white group-data-[state=checked]:border-transparent transition-colors">
                        <img src={BRANDING_PLACEHOLDER} className="max-w-full max-h-full object-contain" />
                      </div>
                      <div>
                        <p className="font-bold leading-tight">
                          {t("createVideo.defaultBranding") || "Default Branding"}
                        </p>
                      </div>
                    </div>
                  </SelectItem>
                )}
                {templates.map((t) => (
                  <SelectItem 
                    key={t.id} 
                    value={t.id} 
                    data-hide-indicator="true"
                    className="py-3 px-4 rounded-xl focus:bg-black/5 data-[state=checked]:bg-black data-[state=checked]:text-white pr-4 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="size-8 rounded-lg bg-black/5 flex items-center justify-center overflow-hidden p-1 border border-black/5 group-data-[state=checked]:bg-white group-data-[state=checked]:border-transparent transition-colors">
                        <img src={t.watermarkUrl} className="max-w-full max-h-full object-contain" />
                      </div>
                      <div>
                        <p className="font-bold leading-tight">{t.name}</p>
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {!isMobile && (
          <div className="flex flex-col-reverse w-full md:w-auto md:flex-row mt-6 md:mt-0 items-center gap-3">
            <Button
              type="button"
              variant="ghost"
              className="w-full md:w-auto rounded-full hover:bg-black hover:text-white px-6 h-12 font-bold transition-all border border-transparent hover:border-black gap-2"
              onClick={() => {
                if (activeTemplate) {
                  handleUpdateTemplate?.();
                } else {
                  setIsSaveTemplateDialogOpen(true);
                }
              }}
            >
              <Save className="size-4" />
              {activeTemplate
                ? (t("createVideo.saveChanges") || "Save Changes")
                : (t("account.templates.watermarkTemplate.saveButton") || "Save as Template")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
